import translationFallback from '@/utilites/translations/translationFallback';
import InstantSearchAlgolia from '../InstantSearchAlgolia';
import { Container, Row } from 'react-bootstrap';
import Head from 'next/head';
import { SkusSearchLayoutProps } from '@/types/other';
import CustomSearchBox from './algoliaCustom/VirtualSearchBox';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import seoConstants from '@/constants/seo.constants';
import { capitalizeWords } from '@/utilites/misc/misc.utils';
import envPublic from '@/config/env.public';

export default function SkusSearchLayout({
  children,
  tCommon,
  metaTitle,
  brandsContent,
  customBaseUrl,
  curationSelectionHighlightSlug,
}: SkusSearchLayoutProps) {
  const router = useRouter();
  const { query, asPath, locales, locale } = router;
  const [stateMeta, setStateMeta] = useState<string | null>(null);
  const canonicalUrl = `${process.env.NEXT_PUBLIC_BASE_URL}${locale === 'en' ? '' : `/${locale}`}${asPath}`;
  const { skus } = query;

  const [brand, setBrand] = useState<string | null>(() => {
    if (Array.isArray(skus)) {
      const brandsIndex = skus.indexOf('brands');
      return brandsIndex !== -1 ? skus[brandsIndex + 1] : null;
    }
    return null;
  });
  const [category, setCategory] = useState<string | null>(() => {
    if (Array.isArray(skus)) {
      const categoryIndex = skus.indexOf('category');
      return categoryIndex !== -1 ? skus[categoryIndex + 1] : null;
    }
    return null;
  });

  const pageTitle = brandsContent?.name
    ? `${translationFallback('sku_title_new1', tCommon('sku_title_new1'), 'Buy and Sell')} ${brandsContent.name} | ${translationFallback('sku_title_new2', tCommon('sku_title_new2'), 'Discover now')} | Spiritory`
    : stateMeta;
  const pageDescription = brandsContent?.name
    ? `${translationFallback('sku_description_new1', tCommon('sku_description_new1'), 'Browse, Buy or Sell')} ${brandsContent.name} ${translationFallback('sku_description_new2', tCommon('sku_description_new2'), 'on the leading marketplace for premium Spirits. With Secure Payment, Buyer Protection and Verfied Bottles')}`
    : translationFallback(
        'sku_description',
        tCommon('sku_description'),
        'Buying and selling collectable whisky and spirits from high-demand brands like Macallan, Ardbeg, Yamazaki, Bowmore, Chichibu, or Springbank has never been easier'
      );

  useEffect(() => {
    const metaTypeValues = Object.values(query) || [];
    const metaType: string[] = [];
    metaTypeValues
      .filter(value => value !== undefined)
      .forEach((value: string | string[]) => {
        if (Array.isArray(value)) {
          metaType.push(...value.map(v => v?.toLowerCase()));
          return;
        }
        metaType.push(value?.toLowerCase());
      });
    let stateMetaType = tCommon('skus_title');

    const includesWhisky = metaType.includes(seoConstants.metaTypeParams.whisky);

    if (metaType.includes(seoConstants.metaTypeParams.rum)) {
      stateMetaType = tCommon('rum_meta_title');
    } else if (metaType.includes(seoConstants.metaTypeParams.gin)) {
      stateMetaType = tCommon('gin_meta_title');
    } else if (metaType.includes(seoConstants.metaTypeParams.other)) {
      stateMetaType = tCommon('other_meta_title');
    } else if (
      metaType.find(value => {
        return seoConstants.metaTypeParams.whiskyName
          .map(whiskyValue => whiskyValue?.toLowerCase())
          .includes(value);
      })
    ) {
      const whiskyName = metaType.find(value => {
        return seoConstants.metaTypeParams.whiskyName
          .map(whiskyValue => whiskyValue?.toLowerCase())
          .includes(value);
      })!;
      stateMetaType = tCommon('buy_and_sell_whisky_name');
      const whiskyNameCapitalized = capitalizeWords(whiskyName);
      stateMetaType = stateMetaType.replace('{{whiskyName}}', whiskyNameCapitalized);
    } else if (metaType.includes(envPublic.NEXT_PUBLIC_ALGOLIA_INDEX?.toLowerCase())) {
      stateMetaType = tCommon('main_algolia_index_title');
    } else if (metaType.includes(envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_WATCHED?.toLowerCase())) {
      stateMetaType = tCommon('most_watched_desc_title');
    } else if (includesWhisky && metaType.includes('scotland')) {
      stateMetaType = tCommon('scotland_whisky_meta_title');
    } else if (includesWhisky && metaType.includes('usa')) {
      stateMetaType = tCommon('usa_whisky_meta_title');
    } else if (includesWhisky && metaType.includes('japan')) {
      stateMetaType = tCommon('japan_whisky_meta_title');
    } else if (includesWhisky && metaType.includes('ireland')) {
      stateMetaType = tCommon('ireland_whisky_meta_title');
    } else if (includesWhisky && metaType.includes('germany')) {
      stateMetaType = tCommon('germany_whisky_meta_title');
    } else if (includesWhisky && metaType.includes('taiwan')) {
      stateMetaType = tCommon('taiwan_whisky_meta_title');
    } else if (includesWhisky) {
      stateMetaType = tCommon('whisky_meta_title');
    }
    setStateMeta(stateMetaType);
  }, [query, tCommon]);

  useEffect(() => {
    if (Array.isArray(skus)) {
      const categoryIndex = skus.indexOf('category');
      setCategory(categoryIndex !== -1 ? skus[categoryIndex + 1] : null);

      const brandsIndex = skus.indexOf('brands');
      setBrand(brandsIndex !== -1 ? skus[brandsIndex + 1] : null);
    }
  }, [skus]);

  return (
    <>
      <Head>
        {stateMeta === null ? (
          <title>
            {metaTitle.metaKey && metaTitle.value
              ? tCommon(metaTitle.key).replace(metaTitle.metaKey, metaTitle.value)
              : tCommon(metaTitle.key)}
          </title>
        ) : (
          <title>{pageTitle}</title>
        )}
        <meta name="description" content={pageDescription} />

        {/* Canonical Tag */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Hreflang Tags */}
        {locales?.map(lang => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang === 'en' ? 'x-default' : lang}
            href={`${process.env.NEXT_PUBLIC_BASE_URL}${lang === 'en' ? '' : `/${lang}`}${asPath}`}
          />
        ))}
        <meta
          name="keywords"
          content={`spiritory, marketplace, buy, sell, ${category ? category + ',' : ''} ${brand ? brand + ',' : ''} ardbeg, rare whiskey, japanese, irish, bourbon`}
        />
      </Head>
      <InstantSearchAlgolia
        isSkusPage={true}
        customBaseUrl={customBaseUrl}
        curationSelectionHighlightSlug={curationSelectionHighlightSlug}
      >
        <Container>
          <Row className="">
            <CustomSearchBox />
            {children}
          </Row>
        </Container>
      </InstantSearchAlgolia>
    </>
  );
}
